import type React from 'react'
import { useParams, Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import {
  useChurch,
  useChurchEvents,
  useFollowStatus,
  useFollowChurch,
  useUnfollowChurch,
  useChurchStats,
  useCampuses,
} from '../hooks/useChurches'
import { useAuthStore } from '../store/auth'
import { PublicLayout } from '../components/layout/PublicLayout'
import { EventBadge } from '../components/ui/EventBadge'
import { getEventType } from '../components/ui/eventTypes'
import { MapPin, Calendar, Clock, Users, CheckCircle, Plus, Share2, Phone, Mail, Globe } from 'lucide-react'
import { ChurchLocationMap } from '../components/ChurchLocationMap'

const GRADIENT = 'linear-gradient(135deg, #3B4CCA 0%, #7C5CD3 100%)'

const sidebarCardStyle: React.CSSProperties = {
  padding: 18,
  background: 'var(--surface)',
  borderRadius: 16,
  border: '1px solid var(--line-soft)',
  boxShadow: '0 2px 8px rgba(31,26,46,.05)',
}

function SidebarCard({
  title,
  children,
  style,
  ...rest
}: {
  title: string
  children: React.ReactNode
  style?: React.CSSProperties
} & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div {...rest} style={{ ...sidebarCardStyle, ...style }}>
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'var(--muted)',
          marginBottom: 10,
        }}
      >
        {title}
      </div>
      {children}
    </div>
  )
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  })
}

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}

function getRecurrenceLabel(rrule: string | null | undefined): string {
  if (!rrule) return 'Recurring'
  const upper = rrule.toUpperCase()
  if (upper.includes('FREQ=WEEKLY')) return 'Weekly'
  if (upper.includes('FREQ=MONTHLY')) return 'Monthly'
  if (upper.includes('FREQ=DAILY')) return 'Daily'
  if (upper.includes('FREQ=YEARLY')) return 'Yearly'
  return 'Recurring'
}

export default function ChurchProfilePage() {
  const { slug } = useParams<{ slug: string }>()
  const { data: churchData, isLoading: churchLoading } = useChurch(slug)
  const { data: potlucksData, isLoading: potlucksLoading } = useChurchEvents(slug)
  const { user } = useAuthStore()
  const { data: followData } = useFollowStatus(slug)
  const followMutation = useFollowChurch(slug ?? '')
  const unfollowMutation = useUnfollowChurch(slug ?? '')
  const { data: statsData } = useChurchStats(slug, Boolean(user))
  const church = churchData?.data
  const { data: campusData } = useCampuses(church?.id)
  const campuses = campusData?.data ?? []
  const allOccurrences = potlucksData?.data ?? []

  // One card per event — show only the next non-cancelled occurrence for recurring events
  const occurrences = (() => {
    const seen = new Set<string>()
    return allOccurrences
      .filter((occ) => !occ.isCancelled)
      .filter((occ) => {
        if (seen.has(occ.event.id)) return false
        seen.add(occ.event.id)
        return true
      })
  })()

  const following = followData?.data?.following ?? false
  const stats = statsData?.data

  if (churchLoading) {
    return (
      <PublicLayout>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: 'calc(100vh - 80px)',
          }}
        >
          <p style={{ fontSize: 13, color: 'var(--muted)' }}>Loading…</p>
        </div>
      </PublicLayout>
    )
  }

  if (!church) {
    return (
      <PublicLayout>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: 'calc(100vh - 80px)',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          <p style={{ fontSize: 14, color: 'var(--ink-2)' }}>Church not found.</p>
          <Link
            to="/churches"
            style={{
              color: 'var(--primary)',
              fontSize: 13,
              textDecoration: 'none',
              fontWeight: 500,
            }}
          >
            ← Back to churches
          </Link>
        </div>
      </PublicLayout>
    )
  }

  const address = [church.addressLine1, church.city, church.state, church.zip]
    .filter(Boolean)
    .join(', ')

  const canonicalUrl = `https://fellowship-app.com/churches/${church.slug}`
  const pageTitle = `${church.name} · Fellowship`
  const pageDescription = church.description
    ? church.description.slice(0, 160)
    : `${church.name}${church.city ? ` in ${church.city}${church.state ? `, ${church.state}` : ''}` : ''} — fellowship events on Fellowship.`
  const pageImage = church.coverImageUrl ?? church.logoUrl ?? undefined

  const churchJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Church',
    name: church.name,
    url: canonicalUrl,
    ...(church.description && { description: church.description }),
    ...(pageImage && { image: pageImage }),
    ...(church.logoUrl && { logo: church.logoUrl }),
    ...(church.websiteUrl && { sameAs: church.websiteUrl }),
    ...(church.phone && { telephone: church.phone }),
    ...(church.email && { email: church.email }),
    address: {
      '@type': 'PostalAddress',
      ...(church.addressLine1 && { streetAddress: church.addressLine1 }),
      ...(church.city && { addressLocality: church.city }),
      ...(church.state && { addressRegion: church.state }),
      ...(church.zip && { postalCode: church.zip }),
    },
  }

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        {pageImage && <meta property="og:image" content={pageImage} />}
        <meta name="twitter:card" content={pageImage ? 'summary_large_image' : 'summary'} />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        {pageImage && <meta name="twitter:image" content={pageImage} />}
        <script type="application/ld+json">{JSON.stringify(churchJsonLd)}</script>
      </Helmet>
    <PublicLayout>
      {/* Unclaimed banner */}
      {church && !church.claimed && (
        <div
          style={{
            background: '#fffbeb',
            borderBottom: '1px solid #fcd34d',
            padding: '12px 40px',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <span style={{ fontSize: 15 }}>⚠️</span>
          <span style={{ fontSize: 13, color: 'var(--ink)', flex: 1 }}>
            This listing was created automatically. Is this your church?
          </span>
          <Link
            to={`/churches/${church.id}/claim`}
            style={{
              padding: '7px 16px',
              borderRadius: 8,
              background: '#d97706',
              color: '#fff',
              fontSize: 13,
              fontWeight: 600,
              textDecoration: 'none',
              flexShrink: 0,
            }}
          >
            Claim this page
          </Link>
        </div>
      )}

      {/* Hero banner */}
      <div
        style={{
          height: 280,
          position: 'relative',
          zIndex: 0,
          overflow: 'hidden',
          background:
            'repeating-linear-gradient(135deg, var(--primary-soft) 0 20px, var(--surface-2) 20px 42px)',
        }}
      >
        {church.coverImageUrl ? (
          <img
            src={church.coverImageUrl}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <div
            style={{
              position: 'absolute',
              right: 40,
              top: 16,
              padding: '6px 12px',
              background: 'var(--surface)',
              border: '1px solid var(--line-soft)',
              borderRadius: 8,
              fontSize: 11,
              color: 'var(--muted)',
              fontFamily: 'monospace',
            }}
          >
            cover photo · 1500 × 500
          </div>
        )}
        {/* Gradient fade to bg */}
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 120,
            background: 'linear-gradient(to bottom, transparent, var(--bg))',
          }}
        />
      </div>

      {/* Main grid */}
      <div
        style={{
          maxWidth: 1240,
          margin: '0 auto',
          padding: '0 20px 60px',
          display: 'grid',
          gap: 28,
          alignItems: 'start',
          position: 'relative',
          zIndex: 1,
        }}
        data-layout="church-profile"
      >
        <style>{`
          @media (min-width: 768px) { [data-layout="church-profile"] { grid-template-columns: minmax(0,1fr) 340px; gap: 36px; padding: 0 40px 60px; } }
        `}</style>
        {/* ── Left column ─────────────────────────────────────────────────── */}
        <div>
          {/* Identity card */}
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 22, marginTop: -52 }}>
            {/* Logo tile */}
            <div
              style={{
                width: 120,
                height: 120,
                borderRadius: 20,
                border: '4px solid var(--bg)',
                background: 'var(--surface)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                flexShrink: 0,
                boxShadow: '0 6px 20px rgba(31,26,46,.12)',
              }}
            >
              {church.logoUrl ? (
                <img
                  src={church.logoUrl}
                  alt={church.name}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <div
                  style={{
                    width: 56,
                    height: 56,
                    background: GRADIENT,
                    borderRadius: 12,
                    opacity: 0.4,
                  }}
                />
              )}
            </div>

            <div style={{ flex: 1, paddingBottom: 6 }}>
              {/* Chips row */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  marginBottom: 8,
                  flexWrap: 'wrap',
                }}
              >
                {church.denomination && (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      height: 22,
                      padding: '0 10px',
                      borderRadius: 999,
                      background: 'var(--surface)',
                      border: '1px solid var(--line-soft)',
                      fontSize: 11.5,
                      color: 'var(--ink-2)',
                      fontWeight: 500,
                    }}
                  >
                    {church.denomination
                      .replace(/-/g, ' ')
                      .replace(/\b\w/g, (c) => c.toUpperCase())}
                  </span>
                )}
                {church.city && (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      height: 22,
                      padding: '0 10px',
                      borderRadius: 999,
                      background: 'var(--surface)',
                      border: '1px solid var(--line-soft)',
                      fontSize: 11.5,
                      color: 'var(--ink-2)',
                      fontWeight: 500,
                    }}
                  >
                    <MapPin size={10} /> {church.city}, {church.state}
                  </span>
                )}
                {church.verified && (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      height: 22,
                      padding: '0 10px',
                      borderRadius: 999,
                      background: '#EEF2FF',
                      border: '1px solid #c7d2fe',
                      fontSize: 11.5,
                      color: '#3730a3',
                      fontWeight: 600,
                    }}
                  >
                    <CheckCircle size={10} /> Verified
                  </span>
                )}
              </div>

              <h1
                style={{
                  margin: '0 0 8px',
                  fontSize: 40,
                  fontWeight: 600,
                  letterSpacing: '-0.025em',
                  lineHeight: 1.05,
                  color: 'var(--ink)',
                }}
              >
                {church.name}
              </h1>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 18,
                  fontSize: 13,
                  color: 'var(--ink-2)',
                }}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <Calendar size={13} />
                  <strong style={{ color: 'var(--ink)' }}>{occurrences.length}</strong> upcoming
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <Users size={13} />
                  <strong style={{ color: 'var(--ink)' }}>{church.followerCount}</strong> followers
                </span>
              </div>
            </div>

            {/* Action buttons */}
            <div
              style={{
                display: 'flex',
                gap: 8,
                paddingBottom: 6,
                alignItems: 'center',
                flexShrink: 0,
              }}
            >
              <button
                style={{
                  height: 38,
                  padding: '0 14px',
                  borderRadius: 999,
                  background: 'transparent',
                  border: '1.5px solid var(--line)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 13,
                  fontWeight: 500,
                  color: 'var(--ink-2)',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                <Share2 size={14} /> Share
              </button>
              <button
                aria-label={following ? 'Unfollow church' : 'Follow church'}
                onClick={() => {
                  if (!user) return
                  if (following) unfollowMutation.mutate()
                  else followMutation.mutate()
                }}
                style={{
                  height: 38,
                  padding: '0 20px',
                  borderRadius: 999,
                  border: 0,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontSize: 13,
                  fontWeight: 600,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  background: following ? 'var(--primary-soft)' : GRADIENT,
                  color: following ? 'var(--primary)' : '#fff',
                  boxShadow: following ? 'none' : '0 4px 12px rgba(59,76,202,.3)',
                }}
              >
                {following ? (
                  <>
                    <CheckCircle size={14} /> Following
                  </>
                ) : (
                  <>
                    <Plus size={14} /> Follow
                  </>
                )}
              </button>
            </div>
          </div>

          <h2
            style={{
              margin: '28px 0 0',
              fontSize: 18,
              fontWeight: 600,
              letterSpacing: '-0.02em',
              color: 'var(--ink)',
            }}
          >
            Upcoming events
            <span style={{ fontWeight: 500, color: 'var(--muted)', fontSize: 15 }}>
              {' '}
              · {occurrences.length}
            </span>
          </h2>

          <div style={{ marginTop: 16 }}>
              {potlucksLoading && <p style={{ fontSize: 13, color: 'var(--muted)' }}>Loading…</p>}
              {!potlucksLoading && occurrences.length === 0 && (
                <p style={{ fontSize: 13, color: 'var(--muted)' }}>No upcoming events scheduled.</p>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {occurrences.map((occ) => {
                  const t = getEventType(occ.event.eventType ?? 'potluck')
                  return (
                    <Link
                      key={occ.id}
                      to={`/events/${occ.event.id}`}
                      style={{ textDecoration: 'none' }}
                    >
                      <article
                        style={{
                          padding: 16,
                          borderRadius: 16,
                          border: '1px solid var(--line-soft)',
                          background: 'var(--surface)',
                          cursor: 'pointer',
                          borderLeft: `3px solid ${t.color}`,
                          transition: 'background 0.12s',
                        }}
                      >
                        <div
                          style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}
                        >
                          <EventBadge type={occ.event.eventType ?? 'potluck'} />
                          {occ.event.isRecurring && (
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                height: 20,
                                padding: '0 8px',
                                borderRadius: 999,
                                background: 'var(--surface-2)',
                                border: '1px solid var(--line-soft)',
                                fontSize: 11,
                                color: 'var(--muted)',
                                fontWeight: 500,
                              }}
                            >
                              {getRecurrenceLabel(occ.event.rrule)}
                            </span>
                          )}
                        </div>
                        <div
                          style={{
                            fontSize: 16,
                            fontWeight: 600,
                            color: 'var(--ink)',
                            lineHeight: 1.2,
                            marginBottom: 6,
                          }}
                        >
                          {occ.event.title}
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            gap: 12,
                            fontSize: 12,
                            color: 'var(--ink-2)',
                            marginBottom: 8,
                          }}
                        >
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <Calendar size={11} /> {formatDate(occ.startsAt)}
                          </span>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <Clock size={11} /> {formatTime(occ.startsAt)}
                          </span>
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                          }}
                        >
                          <span style={{ fontSize: 12, color: 'var(--muted)' }}>
                            {occ.rsvpCount ?? 0} going
                          </span>
                          <span style={{ fontSize: 12, color: 'var(--primary)', fontWeight: 500 }}>
                            View →
                          </span>
                        </div>
                      </article>
                    </Link>
                  )
                })}
                {occurrences.length > 0 && (
                  <div
                    style={{
                      padding: 16,
                      borderRadius: 16,
                      border: '1.5px dashed var(--line-soft)',
                      background: 'transparent',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: 160,
                      cursor: 'default',
                    }}
                  >
                    <span style={{ fontSize: 12, color: 'var(--muted)' }}>
                      Past events archive →
                    </span>
                  </div>
                )}
              </div>
            </div>

        </div>

        {/* ── Right sidebar ────────────────────────────────────────────────── */}
        <aside style={{ paddingTop: 24, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <SidebarCard title="Visit">
            {address && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 12 }}>
                <MapPin size={14} color="var(--muted)" style={{ flexShrink: 0, marginTop: 2 }} />
                <div
                  style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--ink)', lineHeight: 1.5 }}
                >
                  {address}
                </div>
              </div>
            )}
            <ChurchLocationMap
              lat={church.locationLat}
              lng={church.locationLng}
              label={church.name}
            />
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${church.locationLat},${church.locationLng}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-block',
                marginTop: 10,
                fontSize: 12.5,
                fontWeight: 500,
                color: 'var(--primary)',
                textDecoration: 'none',
              }}
            >
              Open in Google Maps →
            </a>
          </SidebarCard>

          {campuses.length > 1 && (
            <SidebarCard title="Campuses">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {campuses.map((campus) => {
                  const campusAddress = [campus.addressLine1, campus.city, campus.state]
                    .filter(Boolean)
                    .join(', ')
                  return (
                    <div
                      key={campus.id}
                      style={{
                        padding: '10px 12px',
                        borderRadius: 10,
                        background: 'var(--surface-2)',
                        border: '1px solid var(--line-soft)',
                      }}
                    >
                      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>
                        {campus.name}
                      </div>
                      {campusAddress && (
                        <div style={{ fontSize: 12, color: 'var(--muted)', display: 'flex', alignItems: 'flex-start', gap: 4 }}>
                          <MapPin size={11} color="var(--muted)" style={{ flexShrink: 0, marginTop: 2 }} />
                          {campusAddress}
                        </div>
                      )}
                      {campus.serviceTimes.length > 0 && (
                        <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {campus.serviceTimes.map((st, i) => (
                            <div key={i} style={{ fontSize: 11.5, color: 'var(--ink-2)', display: 'flex', gap: 4 }}>
                              <Clock size={10} color="var(--muted)" style={{ flexShrink: 0, marginTop: 2 }} />
                              {st.name} · {st.day} {st.time}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </SidebarCard>
          )}

          <SidebarCard title="About">
            {church.description ? (
              <p style={{ margin: 0, fontSize: 14, lineHeight: 1.65, color: 'var(--ink-2)' }}>
                {church.description}
              </p>
            ) : (
              <p style={{ margin: 0, fontSize: 13.5, color: 'var(--muted)', fontStyle: 'italic' }}>
                No description added yet.
              </p>
            )}
            {church.websiteUrl && (
              <a
                href={church.websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  marginTop: church.description ? 12 : 0,
                  fontSize: 13.5,
                  color: 'var(--primary)',
                  textDecoration: 'none',
                  fontWeight: 500,
                }}
              >
                <Globe size={14} />
                {church.websiteUrl.replace(/^https?:\/\//, '')}
              </a>
            )}
          </SidebarCard>

          <SidebarCard title="Contact">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {church.phone && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <Phone size={14} color="var(--muted)" style={{ flexShrink: 0, marginTop: 2 }} />
                  <a
                    href={`tel:${church.phone}`}
                    style={{
                      fontSize: 13.5,
                      fontWeight: 500,
                      color: 'var(--ink)',
                      textDecoration: 'none',
                    }}
                  >
                    {church.phone}
                  </a>
                </div>
              )}
              {church.email && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <Mail size={14} color="var(--muted)" style={{ flexShrink: 0, marginTop: 2 }} />
                  <a
                    href={`mailto:${church.email}`}
                    style={{
                      fontSize: 13.5,
                      fontWeight: 500,
                      color: 'var(--ink)',
                      textDecoration: 'none',
                      wordBreak: 'break-all',
                    }}
                  >
                    {church.email}
                  </a>
                </div>
              )}
              {!church.phone && !church.email && (
                <p style={{ margin: 0, fontSize: 13.5, color: 'var(--muted)', fontStyle: 'italic' }}>
                  No contact details listed yet.
                </p>
              )}
            </div>
          </SidebarCard>

          {occurrences.length > 0 && (
            <SidebarCard title="Coming up">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {occurrences.slice(0, 4).map((occ) => (
                  <div key={occ.id} style={{ display: 'flex', gap: 12, alignItems: 'baseline' }}>
                    <span
                      style={{
                        fontSize: 11,
                        color: 'var(--muted)',
                        fontFamily: 'monospace',
                        minWidth: 48,
                        flexShrink: 0,
                      }}
                    >
                      {formatTime(occ.startsAt)}
                    </span>
                    <span style={{ fontSize: 13, color: 'var(--ink-2)' }}>{occ.event.title}</span>
                  </div>
                ))}
              </div>
            </SidebarCard>
          )}

          {!user && (
            <div
              style={{
                padding: 14,
                borderRadius: 14,
                background: 'var(--primary-soft)',
                border: '1px solid rgba(59,76,202,.15)',
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--primary-deep)', marginBottom: 4 }}>
                Stay in the loop
              </div>
              <div style={{ fontSize: 12, color: 'var(--ink-2)', lineHeight: 1.5, marginBottom: 10 }}>
                Create a free account to follow {church.name} and get notified when new events are posted.
              </div>
              <a
                href="/register"
                style={{
                  display: 'inline-block',
                  padding: '6px 14px',
                  borderRadius: 999,
                  background: GRADIENT,
                  color: '#fff',
                  fontSize: 12,
                  fontWeight: 600,
                  textDecoration: 'none',
                }}
              >
                Join Fellowship
              </a>
            </div>
          )}

          {/* Stats panel (coordinator only) */}
          {stats && (
            <SidebarCard title="Church stats" style={{ marginTop: 0 }} role="region" aria-label="Church stats">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <span style={{ fontSize: 13, color: 'var(--ink-2)' }}>Followers</span>
                  <strong style={{ fontSize: 15 }}>{stats.followerCount}</strong>
                </div>
                {stats.rsvpsByEventType.length > 0 && (
                  <div>
                    <div style={{ fontSize: 11.5, color: 'var(--muted)', marginBottom: 6 }}>
                      RSVPs by type
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {stats.rsvpsByEventType.map(({ eventType, count }) => {
                        const et = getEventType(eventType as never)
                        return (
                          <div
                            key={eventType}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}
                          >
                            <span
                              style={{
                                fontSize: 12,
                                color: 'var(--ink-2)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4,
                              }}
                            >
                              <span aria-label={et.label}>{et.glyph}</span> {et.label}
                            </span>
                            <span style={{ fontSize: 12, fontWeight: 600 }}>{count}</span>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}
                {stats.topOccurrences.length > 0 && (
                  <div>
                    <div style={{ fontSize: 11.5, color: 'var(--muted)', marginBottom: 6 }}>
                      Top events by attendance
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {stats.topOccurrences.map(({ title, rsvpCount }) => (
                        <div
                          key={title}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <span style={{ fontSize: 12, color: 'var(--ink-2)' }}>{title}</span>
                          <span style={{ fontSize: 12, fontWeight: 600 }}>{rsvpCount}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </SidebarCard>
          )}
        </aside>
      </div>
    </PublicLayout>
    </>
  )
}
